<template>
  <div class="system-objects-list-page">
    <!-- Page Header -->
    <PageHeader
      :title="t('myShopData.systemObjectsList.title')"
      :summary="t('myShopData.systemObjectsList.summary')"
    >
      <template #actions>
        <SpmOverlayPanel
          v-if="dataSources.length > 1"
          min-panel-width="320px"
          max-panel-width="420px"
        >
          <template #trigger>
            <Button
              type="button"
              icon="far fa-database"
              class="p-button-outlined"
              :label="t('myShopData.dataSources.title')"
            />
          </template>
          <template #header>
            <div class="ds-panel-header">
              <i class="far fa-database" />
              <span>{{ t('myShopData.dataSources.title') }}</span>
            </div>
          </template>
          <div class="ds-panel-body">
            <div
              v-if="sourcesLoading"
              class="ds-panel-loading"
            >
              <ProgressSpinner
                style="width: 24px; height: 24px"
                stroke-width="6"
              />
            </div>
            <div
              v-else-if="dataSources.length === 0"
              class="ds-panel-empty"
            >
              {{ t('myShopData.dataSources.noSources') }}
            </div>
            <div
              v-else
              class="ds-panel-list"
            >
              <div
                v-for="source in dataSources"
                :key="source.id_data_source"
                class="ds-item"
                :class="{ 'ds-item--child': source.parent_id }"
              >
                <div class="ds-item__left">
                  <i
                    v-if="source.parent_id"
                    class="fa-light fa-arrow-turn-down-right ds-item__indent"
                  />
                  <i
                    v-else-if="hasChildren(source.id_data_source)"
                    class="far fa-folder-tree ds-item__icon ds-item__icon--parent"
                  />
                  <i
                    v-else
                    class="far fa-database ds-item__icon"
                  />
                  <span class="ds-item__label">{{ source.label }}</span>
                </div>
                <div class="ds-item__right">
                  <SpmBadge
                    v-if="source.is_default"
                    :label="t('myShopData.dataSources.default')"
                    :colors="{ bg: '#dbeafe', color: '#1e40af' }"
                    size="sm"
                    variant="soft"
                  />
                  <SpmBadge
                    :label="t(`myShopData.dataSources.types.${source.type}`)"
                    :colors="typeColors[source.type] || { bg: '#f3f4f6', color: '#374151' }"
                    size="sm"
                    variant="soft"
                  />
                  <i
                    v-if="source.active"
                    class="fas fa-check-circle ds-item__status ds-item__status--active"
                  />
                  <i
                    v-else
                    class="fas fa-times-circle ds-item__status ds-item__status--inactive"
                  />
                </div>
              </div>
            </div>
          </div>
        </SpmOverlayPanel>
      </template>
    </PageHeader>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <div class="system-objects-list">
        <SpmTableAPI
          v-if="columns.length > 0"
          ref="tableRef"
          mode="client"
          :data="tableData"
          :columns="columns"
          data-key="type"
          :show-reload-button="true"
          :client-reload-fn="fetchData"
        >
          <!-- Slot for schema_name column -->
          <template #cell-schema_name="{ data }">
            <div class="schema-info">
              <span class="schema-name font-bold">
                {{ t(`myShopData.syncs.${data.schema_name}`) }}
              </span>
              <span
                v-if="data.last_date_sync"
                class="sync-date"
              >
                {{ t('myShopData.syncs.lastDateSync') }} : {{ data.last_date_sync }}
              </span>
              <span
                v-else
                class="sync-date no-sync"
              >
                {{ t('myShopData.syncs.noSync') }}
              </span>
            </div>
          </template>

          <!-- Direct view action column -->
          <template #cell-actions="{ data }">
            <Button
              type="button"
              icon="far fa-eye"
              class="p-button-text p-button-sm p-button-rounded view-btn"
              :title="t('myShopData.syncs.viewData')"
              @click="navigateToSchema(data)"
            />
          </template>
        </SpmTableAPI>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmOverlayPanel from '@/components/spm-primevue/SpmOverlayPanel.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import type { SpmColumnConfig } from '@/components/table-rest-api/types';
import { UserState } from '@/composables/User';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  fetchSystemSchemas,
  SchemaItem,
} from '@/composables/custom-data/customData';

interface DataSource {
  id_data_source: number;
  label: string;
  type: string;
  is_default: boolean;
  active: boolean;
  parent_id: number | null;
}

export default defineComponent({
  name: 'SystemObjectsList',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmOverlayPanel,
    SpmBadge,
    Button,
    ProgressSpinner,
  },

  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const tableRef = ref();

    // System schemas table
    const tableData = ref<SchemaItem[]>([]);

    // Data sources overlay
    const dataSources = ref<DataSource[]>([]);
    const sourcesLoading = ref(true);

    const typeColors: Record<string, { bg: string; color: string }> = {
      ecommerce_connector: { bg: '#dcfce7', color: '#166534' },
      api: { bg: '#e0e7ff', color: '#3730a3' },
      manual: { bg: '#fef3c7', color: '#92400e' },
    };

    const childSet = ref<Set<number>>(new Set());

    const hasChildren = (id: number): boolean => childSet.value.has(id);

    const fetchDataSources = async () => {
      sourcesLoading.value = true;
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/data-sources',
          {},
          '',
          undefined,
          true,
        );
        if (response && response.data) {
          dataSources.value = response.data;
          const parents = new Set<number>();
          response.data.forEach((ds: DataSource) => {
            if (ds.parent_id) parents.add(ds.parent_id);
          });
          childSet.value = parents;
        }
      } finally {
        sourcesLoading.value = false;
      }
    };

    const fetchData = async () => {
      tableData.value = await fetchSystemSchemas(UserState.activeShop?.id ?? 0);
      return tableData.value;
    };

    const reloadTableData = async () => {
      if (tableRef.value) {
        await tableRef.value.reloadData();
      }
    };

    const navigateToSchema = (rowData: SchemaItem) => {
      router.push({ name: `shop.syncs.${rowData.type}` });
    };

    const columns: SpmColumnConfig[] = [
      {
        id: 'schema_name',
        header: t('myShopData.syncs.schema_name'),
        fields: [
          {
            name: 'schema_name',
            label: t('myShopData.syncs.schema_name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'last_date_sync',
        ],
      },
      {
        id: 'actions',
        header: '',
        fields: ['type'],
        maxWidth: '60px',
      },
    ];

    onMounted(() => {
      reloadTableData();
      fetchDataSources();
    });

    return {
      t,
      router,
      tableRef,
      tableData,
      fetchData,
      columns,
      navigateToSchema,
      dataSources,
      sourcesLoading,
      typeColors,
      hasChildren,
    };
  },
});
</script>

<style lang="scss" scoped>
.system-objects-list-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.schema-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.schema-name {
  font-weight: 500;
  color: var(--text-color);
}

.sync-date {
  font-size: 0.75rem;
  color: #667085;

  &.no-sync {
    color: #98a2b3;
    font-style: italic;
  }
}

.system-objects-list {
  position: relative;
  min-height: 200px;
}

.view-btn {
  color: #667085 !important;

  &:hover {
    color: #344054 !important;
    background: #f2f4f7 !important;
  }
}

/* Data sources overlay panel */
.ds-panel-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: #1f2937;

  i {
    color: #6b7280;
  }
}

.ds-panel-body {
  padding: 0.5rem 0;
  max-height: 400px;
  overflow-y: auto;
}

.ds-panel-loading {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
}

.ds-panel-empty {
  padding: 1.5rem 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.8125rem;
}

.ds-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  gap: 0.75rem;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }

  &--child {
    padding-left: 1.75rem;
  }
}

.ds-item__left {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.ds-item__icon {
  color: #6b7280;
  font-size: 0.8rem;
  flex-shrink: 0;

  &--parent {
    color: #7c3aed;
  }
}

.ds-item__indent {
  color: #9ca3af;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.ds-item__label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ds-item__right {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
}

.ds-item__status {
  font-size: 0.75rem;

  &--active {
    color: #94c840;
  }

  &--inactive {
    color: #d1d5db;
  }
}
</style>
